import React, {Fragment, useState} from 'react';
import { useToast } from '../../context/ToastContext';

function BatchSelectionModal({ batches, onSelect, onClose, onAddShortbook }) {
  const toast = useToast();
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customBatch, setCustomBatch] = useState({
    mrp: '',
    quantity: ''
  });
  const [batchQuantities, setBatchQuantities] = useState({});

  // Check if batch expires within 6 months
  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    const sixMonthsFromNow = new Date();
    sixMonthsFromNow.setMonth(today.getMonth() + 6);
    
    return expiry <= sixMonthsFromNow && expiry >= today;
  };

  const handleCustomBatchSubmit = () => {
    if (!customBatch.mrp || !customBatch.quantity) {
      toast.error('Please fill all required fields.');
      return;
    }

    const formattedBatch = {
      id: `custom-batch-${Date.now()}`,
      batch: 'Pending', // Default value for batch
      expiry: '',
      expiry_label: '-',
      mrp: parseFloat(customBatch.mrp),
      price_to_retailer: 0, // Default to 0 for custom batches
      stock: '-', // Changed: Don't show stock for custom batch
      quantity: parseInt(customBatch.quantity), // This is the order quantity
      orderQuantity: parseInt(customBatch.quantity) // Add this to maintain consistency with API batches
    };

    onSelect(formattedBatch);
  };

  const handleQuantityChange = (batchId, value, maxStock) => {
    if (value > maxStock) {
      toast.info(`Maximum available stock is ${maxStock}.`);
      value = maxStock;
    }
    setBatchQuantities({
      ...batchQuantities,
      [batchId]: value
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-[600px] max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-800">
            {showCustomForm ? 'Add Medicine' : 'Select Batch'}
          </h3>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <i className="material-icons-outlined">close</i>
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div className="overflow-y-auto p-4">
          {!showCustomForm ? (
            <>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Batch</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Expiry</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">MRP</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {batches.map(batch => {
                    const expiringSoon = isExpiringSoon(batch.expiry);
                    return (
                      <tr 
                        key={batch.id} 
                        className={`hover:bg-gray-50 ${expiringSoon ? 'bg-yellow-50 border-l-4 border-yellow-400' : ''}`}
                      >
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {batch.batch}
                            {expiringSoon && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                Expiring Soon
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={expiringSoon ? 'text-yellow-700 font-semibold' : ''}>
                            {batch.expiry_label}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">₹{batch.mrp}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`font-medium ${parseInt(batch.quantity) < 10 ? 'text-red-600' : 'text-green-600'}`}>
                            {batch.quantity}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <input
                            type="number"
                            min="1"
                            max={batch.stock}
                            value={batchQuantities[batch.id] || 1}
                            className="input-field w-16 h-8 px-2"
                            onChange={(e) => handleQuantityChange(batch.id, parseInt(e.target.value) || 1, batch.stock)}
                          />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right">
                          <button
                            onClick={() => {
                              const selectedBatch = {
                                ...batch,
                                quantity: batchQuantities[batch.id] || 1,
                                orderQuantity: batchQuantities[batch.id] || 1,
                                price_to_retailer: batch.price_to_retailer
                              };
                              onSelect(selectedBatch);
                            }}
                            className="btn-primary text-sm px-3 py-1"
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <button
                onClick={() => setShowCustomForm(true)}
                className="w-full mt-4 py-2 text-primary-600 hover:bg-gray-50 border rounded-lg font-medium flex items-center justify-center space-x-2"
              >
                <i className="material-icons-outlined text-base">add</i>
                <span>Add Without Batch</span>
              </button>
            </>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  MRP*
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="input-field"
                  value={customBatch.mrp}
                  onChange={(e) => setCustomBatch({ ...customBatch, mrp: e.target.value })}
                  placeholder="Enter MRP"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  How many items to add?*
                </label>
                <input
                  type="number"
                  min="1"
                  className="input-field"
                  value={customBatch.quantity}
                  onChange={(e) => setCustomBatch({ ...customBatch, quantity: e.target.value })}
                  placeholder="Enter quantity"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4">
          <div className="flex justify-start space-x-3">
            {!showCustomForm ? (
                <button
                    onClick={onAddShortbook}
                    className="btn-secondary"
                >
                  Add to shortbook
                </button>
            ) : (
                <Fragment/>
            )}
          </div>
          <div className="flex justify-end space-x-3">
            {showCustomForm ? (
                <>
                  <button
                      onClick={() => setShowCustomForm(false)}
                      className="btn-secondary"
                  >
                    Back
                  </button>
                  <button
                      onClick={handleCustomBatchSubmit}
                      className="btn-primary"
                  >
                    Add Medicine
                  </button>
                </>
            ) : (
                <button
                    onClick={onClose}
                    className="btn-danger"
                >
                  Cancel
                </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BatchSelectionModal;